import { cn } from '@/lib/utils';

import type { DataTableColumn, DataTableRow } from '../types';

export type PinnedSide = 'left' | 'right';

/**
 * Enforce the pinning constraints and move pinned columns to the table
 * edges regardless of the stored column order:
 * - at most one pinned column per side (first one in order wins, the
 *   rest lose their pin);
 * - the manually rendered actions column is always the right-most cell,
 *   so while it is pinned a right-pinned data column is ignored (its
 *   sticky offset cannot be derived from the auto-width actions cell).
 */
export function applyColumnPinning<T extends DataTableRow>(
  columns: DataTableColumn<T>[],
  hasPinnedActions = false
): DataTableColumn<T>[] {
  let left: DataTableColumn<T> | undefined;
  let right: DataTableColumn<T> | undefined;
  const middle: DataTableColumn<T>[] = [];

  for (const col of columns) {
    if (col.pinned === 'left' && !left) {
      left = col;
    } else if (col.pinned === 'right' && !right && !hasPinnedActions) {
      right = col;
    } else if (col.pinned) {
      middle.push({ ...col, pinned: undefined });
    } else {
      middle.push(col);
    }
  }

  return [...(left ? [left] : []), ...middle, ...(right ? [right] : [])];
}

interface PinnedCellClassOptions {
  /** Header cells stack above body cells while both stick */
  header?: boolean;
  /** Offset a left-pinned cell by the pinned selection-checkbox column (w-12) */
  afterCheckbox?: boolean;
  /** Draw the border/shadow separator on the scroll-facing edge (off for inner pinned cells like the checkbox) */
  edge?: boolean;
}

/**
 * position:sticky classes for a pinned cell. Neighbouring cells slide
 * underneath a stuck cell, so it must paint opaquely: an opaque
 * `before:` layer carries the card background and a translucent
 * `after:` layer reproduces the row hover — the row's own translucent
 * `bg-muted/50` hover would let the covered cells show through. Body
 * rows must carry the `group` class for the hover overlay to fire.
 */
export function pinnedCellClasses(
  pinned: PinnedSide | undefined,
  { header = false, afterCheckbox = false, edge = true }: PinnedCellClassOptions = {}
): string {
  if (!pinned) return '';

  return cn(
    'sticky',
    pinned === 'left' ? (afterCheckbox ? 'left-12' : 'left-0') : 'right-0',
    header ? 'z-[2]' : 'z-[1]',
    'before:absolute before:inset-0 before:-z-10 before:bg-card',
    'after:absolute after:inset-0 after:-z-10 after:pointer-events-none',
    header ? 'hover:after:bg-muted/50' : 'group-hover:after:bg-muted/50',
    // Edge separator lives on the before: layer, not on the cell's own border, so it shows
    // only while the scroll container (TableScrollArea) reports hidden content on that side —
    // a table that fits renders no separator at all.
    edge &&
      (pinned === 'left'
        ? '[[data-scroll-start]_&]:before:border-r [[data-scroll-start]_&]:before:border-border [[data-scroll-start]_&]:before:shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)]'
        : '[[data-scroll-end]_&]:before:border-l [[data-scroll-end]_&]:before:border-border [[data-scroll-end]_&]:before:shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.15)]')
  );
}
